// Define router switch
import React, { useEffect, useRef } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Box from "@mui/material/Box";
import { Helmet } from "react-helmet";
import { Outlet } from "react-router";

import { RefreshTimer } from "merchant-portal-components/RefreshTimer/RefreshTimer";
import { tracking } from "merchant-portal-frontend/src/analytics";
import { useFlags } from "merchant-portal-frontend/src/feature-flags";
import { AlertProvider } from "merchant-portal-frontend/src/providers/AlertProvider";
import { AuthProvider } from "merchant-portal-frontend/src/providers/AuthProvider";
import { BackendProvider } from "merchant-portal-frontend/src/providers/BackendProvider";
import ThemeProvider from "merchant-portal-frontend/src/providers/ThemeProvider";
import { GlobalStyle } from "merchant-portal-theme/GlobalStyle";
import { useTheme } from "merchant-portal-theme/useTheme";

export const addErrorContext = (context) => {
	const entries = Object.entries(context);
	entries.forEach((entry) => datadogRum.setGlobalContextProperty(entry[0], entry[1]));
};

const GlobalErrorContext = () => {
	const flags = useFlags();

	useEffect(() => {
		addErrorContext({ flags });
	}, [flags]);

	return null;
};

const AppPageTitle = () => {
	const { appPageTitle, favicon } = useTheme();
	const hasRegisteredPage = useRef(""); // Used to track if the page has been registered, this prevents multiple calls to register the same page during rerenders

	return (
		<Helmet
			onChangeClientState={(newState) => {
				if (hasRegisteredPage.current !== newState.title) {
					tracking.registerPage(newState.title);
					hasRegisteredPage.current = newState.title; // Mark as called
				}
			}}
		>
			<title>{appPageTitle}</title>
			{process.env.NODE_ENV !== "test" && (
				<link rel="icon" type="image/png" href={favicon} sizes="16x16" />
			)}
		</Helmet>
	);
};

export function RootPage() {
	return (
		<ThemeProvider>
			<AppPageTitle />
			<AlertProvider>
				<AuthProvider>
					<BackendProvider>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								width: "100%",
							}}
							id="app"
						>
							<Outlet />
							<RefreshTimer />
							<GlobalErrorContext />
							<GlobalStyle />
						</Box>
					</BackendProvider>
				</AuthProvider>
			</AlertProvider>
		</ThemeProvider>
	);
}

export default RootPage;
